/* Theme-owned entrance animation. Source contract: animation-contract.json.
 * Reinitialization is allowed; there is deliberately no permanent played flag.
 */
(function installNativeSustainEntryAnimation() {
  'use strict';

  // Several native sections may include this asset. Register lifecycle hooks once.
  if (window.NativeSustainEntryAnimation) return;

  const selector = '[data-native-sustain-entry]';
  const visibleRatio = 0.25;
  const animatedClass = 'npsu-ef245220474b';
  const currentState = new WeakMap();

  function cancelPreviousState(element) {
    const previous = currentState.get(element);
    if (!previous) return;
    if (previous.observer) previous.observer.unobserve(element);
    previous.cleanupTimers.forEach(timer => window.clearTimeout(timer));
    previous.cleanupTimers.clear();
  }

  function play(element, animation, state) {
    if (currentState.get(element) !== state) return;
    element.style.visibility = 'visible';
    element.classList.add(animatedClass, animation);
    element.onanimationend = function onAnimationEnd() {
      if (currentState.get(element) !== state) return;
      const timer = window.setTimeout(function removeAnimatedClass() {
        state.cleanupTimers.delete(timer);
        if (currentState.get(element) === state) {
          element.classList.remove(animatedClass);
        }
      }, 2000);
      state.cleanupTimers.add(timer);
    };
  }

  function isVisibleEnough(entry) {
    if (!entry.isIntersecting) return false;
    const rootHeight = entry.rootBounds
      ? entry.rootBounds.height
      : window.innerHeight;
    return entry.intersectionRatio >= visibleRatio ||
      entry.boundingClientRect.height * visibleRatio > rootHeight;
  }

  function init(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const elements = Array.from(root.querySelectorAll(selector));
    if (typeof root.matches === 'function' && root.matches(selector)) {
      elements.unshift(root);
    }
    const observers = new Map();

    elements.forEach(function initializeElement(element) {
      const animation = element.getAttribute('data-native-sustain-entry');
      if (!animation) return;
      cancelPreviousState(element);

      const state = { observer: null, cleanupTimers: new Set() };
      currentState.set(element, state);
      if (animation.toLowerCase().includes('in')) {
        element.style.visibility = 'hidden';
      }

      if (!window.IntersectionObserver) {
        play(element, animation, state);
        return;
      }

      const offset = Number(element.getAttribute('data-native-offset')) || 0;
      let observer = observers.get(offset);
      if (!observer) {
        observer = new window.IntersectionObserver(function handleIntersections(entries, activeObserver) {
          entries.forEach(function handleEntry(entry) {
            const activeState = currentState.get(entry.target);
            if (!activeState || activeState.observer !== activeObserver) return;
            if (!isVisibleEnough(entry)) return;
            play(entry.target, entry.target.getAttribute('data-native-sustain-entry'), activeState);
            activeObserver.unobserve(entry.target);
            activeState.observer = null;
          });
        }, {
          threshold: [0, visibleRatio],
          rootMargin: `0px 0px ${-offset}px 0px`
        });
        observers.set(offset, observer);
      }
      state.observer = observer;
      observer.observe(element);
    });
  }

  window.NativeSustainEntryAnimation = { init };
  document.addEventListener('shopify:section:load', function onSectionLoad(event) {
    init(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() {
      init(document);
    }, { once: true });
  } else {
    init(document);
  }
})();

/* Native controls for the two observed HTML video contracts. */
(() => {
  'use strict';
  if (window.NativeSustainMedia) return;
  const hidden = 'npsu-859d5516219a';
  const playButton = 'npsu-46a33dc3c3da';
  const coverButton = 'npsu-764c7f3a2cd8';
  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-native-sustain-page]')];
    if (scope.matches?.('[data-native-sustain-page]')) roots.unshift(scope);
    roots.forEach(root => {
      if (root.dataset.nativeMediaReady) return;
      root.dataset.nativeMediaReady = 'true';
      root.querySelectorAll('[data-native-kind="HTML.Video"] > video').forEach(video => {
        let started = false;
        const controls = video.hasAttribute('controls');
        video.addEventListener('play', () => {
          started = true;
          if (video.nextElementSibling) video.nextElementSibling.style.opacity = '0';
        });
        video.addEventListener('pause', () => {
          if (video.nextElementSibling) video.nextElementSibling.style.opacity = '1';
        });
        video.parentElement.onclick = event => {
          if (!controls || !started || event.target !== video) {
            if (video.paused) video.play(); else video.pause();
            if (video.nextElementSibling) video.nextElementSibling.style.opacity = video.paused ? '1' : '0';
            return false;
          }
        };
      });
      root.querySelectorAll('[data-native-kind="HTML.Video2"]').forEach(container => {
        const video = container.querySelector('video');
        if (!video) return;
        function bind() {
          const cover = container.querySelector('.' + coverButton);
          const play = container.querySelector('.' + playButton);
          const autoplay = video.hasAttribute('autoplay');
          const controls = video.hasAttribute('controls');
          function loadSource() {
            const source = video.getAttribute('data-src');
            if (source) { video.src = source; video.removeAttribute('data-src'); }
          }
          if (!cover || autoplay) loadSource();
          video.addEventListener('loadeddata', () => video.classList.remove(hidden));
          container.addEventListener('click', () => {
            if (!video.getAttribute('src')) loadSource();
            if (cover && !cover.classList.contains(hidden)) {
              video.play(); cover.classList.add(hidden);
            } else if (!controls) {
              if (video.paused) video.play(); else video.pause();
            }
          });
          video.addEventListener('pause', () => play?.classList.remove(hidden));
          video.addEventListener('play', () => play?.classList.add(hidden));
          if (autoplay && video.hasAttribute('muted')) {
            cover?.classList.add(hidden); play?.classList.add(hidden); video.play();
          }
        }
        if (video.getAttribute('data-lazyloading') === 'lazy' && window.IntersectionObserver) {
          const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting) { bind(); observer.unobserve(container); }
          }), {threshold: 0.3});
          observer.observe(container);
        } else bind();
      });
      root.querySelectorAll('[data-native-href]').forEach(element => {
        element.addEventListener('click', event => {
          event.stopPropagation();
          window.open(element.getAttribute('data-native-href'), event.ctrlKey || event.metaKey ? '_blank' : element.getAttribute('data-target') || '_self');
        });
      });
    });
  }
  window.NativeSustainMedia = {init};
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();

/* Native horizontal rail for this page's three bounded, non-looping Slider2s.
 * Source geometry, dot grouping, arrow step and height policy are separate.
 */
(() => {
  'use strict';
  if (window.NativeSustainSliders) return;
  const cls = {
    rail:'npsu-3ed44f748267', track:'npsu-82e28b149c8e', slide:'npsu-17726111c326',
    prev:'npsu-251594ed3037', next:'npsu-7e42e91b576e', dots:'npsu-064798aa9f3f',
    dot:'npsu-34356a431b59', active:'np-sustain-active', disabled:'np-sustain-disabled',
    visible:'npsu-d42ef1497900', draggable:'npsu-8128c9a0046e', drag:'npsu-4ac5248b70ac'
  };
  function init(scope = document) {
    scope.querySelectorAll('[data-native-sustain-page] [data-native-kind="Slider2"]').forEach(root => {
      if (root.dataset.nativeSliderReady) return;
      root.dataset.nativeSliderReady = 'true';
      const config = JSON.parse(root.getAttribute('data-native-slider-config').replace(/'/g,'"'));
      if (config.loop || config.autoPlay || config.maxHeight) throw new Error('Unreviewed slider contract');
      const rail = root.querySelector('.' + cls.rail);
      const slides = [...rail.children];
      const track = document.createElement('div'); track.className = cls.track;
      slides.forEach(slide => { slide.classList.add(cls.slide); track.append(slide); }); rail.append(track);
      const prev = root.querySelector('.' + cls.prev), next = root.querySelector('.' + cls.next), dots = root.querySelector('.' + cls.dots);
      let show = 1, step = 1, width = 0, itemWidth = 0, animation = 0, scrollTimer = null;
      let mouseX = null, dragged = false, preventClick = false, lastPage = -1;
      function device() { return innerWidth >= 1200 ? 'all' : innerWidth >= 1025 ? 'laptop' : innerWidth >= 768 ? 'tablet' : 'mobile'; }
      function currentSlide() { return Math.round(rail.scrollLeft / itemWidth) || 0; }
      function measureHeight() {
        const visible = slides.filter(slide => slide.classList.contains(cls.visible));
        const inners = visible.map(slide => slide.querySelector('[data-native-kind="Slide2"]'));
        inners.forEach(inner => inner?.removeAttribute('style'));
        const height = Math.max(0,...inners.map(inner => inner?.getBoundingClientRect().height || 0));
        if (height) track.style.height = height + 'px';
        inners.forEach(inner => { if (inner) inner.style.height = '100%'; });
      }
      function update() {
        const left = rail.scrollLeft, end = Math.ceil(left + width) >= Math.floor(itemWidth * slides.length);
        if (prev) { prev.classList.toggle(cls.disabled,left <= 0 || slides.length <= show); prev.setAttribute('aria-disabled',String(left <= 0 || slides.length <= show)); }
        if (next) { next.classList.toggle(cls.disabled,end || slides.length <= show); next.setAttribute('aria-disabled',String(end || slides.length <= show)); }
        const page = end ? Math.ceil(slides.length / show) - 1 : Math.round(left / width);
        slides.forEach((slide,index) => {
          slide.classList.toggle(cls.active,currentSlide() === index);
          slide.classList.toggle(cls.visible,Math.ceil(index * itemWidth) >= Math.floor(left) && Math.floor((index + 1) * itemWidth) <= Math.ceil(left + width));
        });
        if (dots && page !== lastPage) [...dots.children].forEach((dot,index) => dot.classList.toggle(cls.active,index === page));
        lastPage = page; measureHeight();
      }
      function move(index, immediate = false) {
        const target = Math.min(Math.max(index * itemWidth,0),Math.max(0,(slides.length - show) * itemWidth));
        const duration = immediate ? 0 : 0.5 * Math.abs(rail.scrollLeft - target);
        const token = ++animation, start = performance.now();
        function frame(now) {
          if (animation !== token) return;
          const elapsed = now - start;
          if (!duration || elapsed >= duration) { rail.scrollLeft = target; update(); return; }
          rail.scrollLeft += (target - rail.scrollLeft) * (elapsed / duration) ** 2;
          update(); requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
      }
      function layout() {
        const index = currentSlide(); width = rail.clientWidth;
        const mode = device(); show = config.slidesToShow[mode]; step = config.slidesToScroll[mode];
        itemWidth = width / show;
        slides.forEach(slide => { slide.style.height = 'auto'; slide.style.width = itemWidth + 'px'; });
        track.style.width = itemWidth * slides.length + 'px';
        rail.classList.toggle(cls.draggable,innerWidth >= 768);
        if (dots) {
          dots.replaceChildren();
          for (let page = 0; page < Math.ceil(slides.length / show); page++) {
            const dot = document.createElement('button'); dot.className = cls.dot; dot.dataset.index = String(page);
            dot.setAttribute('aria-label','Page ' + (page + 1)); dot.setAttribute('role','tab');
            dot.addEventListener('click',event => { event.preventDefault(); move(Math.round(page * width / itemWidth)); }); dots.append(dot);
          }
        }
        lastPage = -1; root.setAttribute('style','visibility: visible; opacity: 1;'); move(index,true); update();
      }
      prev?.addEventListener('click',event => { event.preventDefault(); move(currentSlide() - step); });
      next?.addEventListener('click',event => { event.preventDefault(); move(currentSlide() + step); });
      rail.addEventListener('scroll',event => {
        event.stopPropagation(); update(); clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (mouseX === null && itemWidth && Math.abs(rail.scrollLeft / itemWidth - currentSlide()) > 0.02 && itemWidth * slides.length > width + rail.scrollLeft) move(currentSlide());
        },150);
      });
      rail.addEventListener('mousedown',event => {
        if (innerWidth < 768 || ['SELECT','INPUT','TEXTAREA'].includes(event.target.tagName)) return;
        event.preventDefault(); event.stopPropagation(); mouseX = event.clientX; dragged = false; rail.classList.add(cls.drag);
      });
      rail.addEventListener('mousemove',event => {
        if (mouseX === null) return;
        dragged = true; ++animation; rail.scrollLeft += (mouseX - event.clientX) * 1.1; mouseX = event.clientX;
      });
      const endDrag = () => { mouseX = null; rail.classList.remove(cls.drag); if (dragged) preventClick = true; dragged = false; };
      rail.addEventListener('mouseup',endDrag); rail.addEventListener('mouseleave',endDrag);
      rail.addEventListener('click',event => { if (preventClick) { event.preventDefault(); event.stopPropagation(); } preventClick = false; });
      root.querySelectorAll('img').forEach(image => { image.addEventListener('load',measureHeight); image.addEventListener('error',measureHeight); });
      window.addEventListener('resize',layout); layout();
      if (window.ResizeObserver) {
        let timer;
        const observer = new ResizeObserver(() => { clearTimeout(timer); timer = setTimeout(measureHeight,100); });
        root.querySelectorAll('[data-native-kind="Slide2"] > div').forEach(container => observer.observe(container));
      }
      [500,1000,1500].forEach(delay => setTimeout(layout,delay));
    });
  }
  window.NativeSustainSliders = {init};
  document.addEventListener('shopify:section:load',event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init(),{once:true});
  else init();
})();

/* Two source FAQ generations keep their distinct state and timing contracts. */
(() => {
  'use strict';
  if (window.NativeSustainFAQ) return;
  const imagesReady = panel => Promise.all([...panel.querySelectorAll('img')].map(image => image.complete ? Promise.resolve() : new Promise(resolve => {
    image.addEventListener('load', resolve, {once:true});
    image.addEventListener('error', resolve, {once:true});
  })));
  // The source measures after the opening animation has entered visible layout.
  function whenVisible(panel, callback) {
    if (!window.IntersectionObserver) { callback(); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.intersectionRatio > 0) { callback(); observer.disconnect(); }
    }));
    observer.observe(panel);
  }
  const classes = {
    wrapper:'npsu-2753c20e944f', show:'npsu-2ec967685c24',
    hide:'npsu-7736dfa84d89', opening:'npsu-e7b525cea995',
    closing:'npsu-aa7c20ef824e', open:'npsu-2348f9987442'
  };
  function init(scope = document) {
    scope.querySelectorAll('[data-native-sustain-page] [data-native-kind="Accordion"]').forEach(group => {
      if (group.dataset.nativeFAQReady) return;
      group.dataset.nativeFAQReady = 'true';
      const items = [...group.children].map(item => {
        const header = item.querySelector('[data-native-kind="Accordion.Header"]');
        const panel = item.querySelector('[data-native-kind="Accordion.Content"]');
        const wrapper = document.createElement('div'); wrapper.className = classes.wrapper;
        const measure = () => { wrapper.style.height = header.getAttribute('data-active') === 'true' ? panel.clientHeight + 'px' : '0px'; };
        measure(); panel.style.display = ''; wrapper.classList.add(header.getAttribute('data-active') === 'true' ? classes.show : classes.hide);
        wrapper.append(panel); item.append(wrapper);
        if (window.MutationObserver) new MutationObserver(measure).observe(panel, {subtree:true, attributes:true});
        panel.querySelectorAll('img').forEach(image => { image.addEventListener('load', measure); image.addEventListener('error', measure); });
        whenVisible(panel, () => { measure(); imagesReady(panel).then(measure); });
        return {header,panel,wrapper,measure};
      });
      function close(item, animationHost = item.wrapper) {
        item.header.setAttribute('data-active','false');
        item.wrapper.classList.remove(classes.show, classes.opening);
        item.wrapper.classList.add(classes.closing);
        animationHost.addEventListener('animationend', () => {
          item.wrapper.classList.add(classes.hide); item.wrapper.classList.remove(classes.closing);
        }, {once:true});
      }
      items.forEach(item => item.header.addEventListener('click', event => {
        event.stopPropagation();
        if (item.header.getAttribute('data-active') === 'true') close(item);
        else {
          item.header.setAttribute('data-active','true');
          item.wrapper.classList.remove(classes.closing,classes.hide);
          item.wrapper.classList.add(classes.opening);
          whenVisible(item.panel, () => imagesReady(item.panel).then(item.measure));
          item.wrapper.addEventListener('animationend', () => {
            item.wrapper.classList.add(classes.show); item.wrapper.classList.remove(classes.opening);
          }, {once:true});
        }
        if (group.getAttribute('data-multiple') === 'false') items.filter(other => other !== item).forEach(other => close(other, item.wrapper));
      }));
    });
    scope.querySelectorAll('[data-native-sustain-page] [data-native-kind="Accordion3"]').forEach(group => {
      if (group.dataset.nativeFAQReady) return;
      group.dataset.nativeFAQReady = 'true';
      const items = [...group.querySelectorAll(':scope > details')];
      function scrollHeader(header) {
        window.scrollTo({top: header.getBoundingClientRect().top + window.scrollY - 50 - header.offsetHeight, behavior:'smooth'});
      }
      function toggle(item, close, scroll) {
        const header = item.querySelector('[data-native-kind="Accordion3.Header"]');
        if (close) {
          item.classList.remove(classes.open); header.setAttribute('data-active','false');
          setTimeout(() => { item.open = false; if (scroll) scrollHeader(header); }, 300);
        } else {
          item.open = true; header.setAttribute('data-active','true');
          setTimeout(() => item.classList.add(classes.open), 10);
          if (scroll) setTimeout(() => scrollHeader(header), 300);
        }
      }
      function bind(header) {
        header.parentElement.addEventListener('click', event => {
          event.preventDefault(); const item = header.closest('details');
          const other = group.querySelector(':scope > details[open]');
          if (group.getAttribute('data-open-multiple') === 'false' && other && other !== item) toggle(other,true,false);
          toggle(item,item.hasAttribute('open'),group.getAttribute('data-scroll-to-top') === 'true');
        });
      }
      const headers = items.map(item => item.querySelector('[data-native-kind="Accordion3.Header"]'));
      if (window.IntersectionObserver) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) { bind(entry.target); observer.unobserve(entry.target); }
        })); headers.forEach(header => observer.observe(header));
      } else headers.forEach(bind);
    });
  }
  window.NativeSustainFAQ = {init};
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();

/* Native lazy-background lifecycle; source evidence is background-contract.json. */
(function installNativeSustainBackground() {
  'use strict';
  if (window.NativeSustainBackground) return;
  const selector = '.npsu-57fdb6011cd3';
  const marker = 'npsu-57fdb6011cd3';
  function elements(root) {
    const list = Array.from(root.querySelectorAll(selector));
    if (typeof root.matches === 'function' && root.matches(selector)) list.unshift(root);
    return list;
  }
  function init(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    window.setTimeout(function initializeObserver() {
      if ('IntersectionObserver' in window) {
        const observer = new window.IntersectionObserver(function reveal(entries) {
          entries.forEach(function onEntry(entry) {
            if (entry.isIntersecting) {
              entry.target.classList.remove(marker);
              observer.unobserve(entry.target);
            }
          });
        }, {rootMargin: '5%'});
        elements(root).forEach(element => observer.observe(element));
      } else {
        window.setTimeout(function noObserverFallback() {
          elements(root).forEach(element => element.classList.remove(marker));
        }, 3000);
      }
    });
    window.setTimeout(function maximumDelayFallback() {
      elements(root).forEach(element => element.classList.remove(marker));
    }, 10000);
  }
  window.NativeSustainBackground = {init};
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  } else {
    init(document);
  }
})();
